/* ============================================================================
   <unc-journey>
   The 30-day journey, on /pages/journey, behind a Shopify login.

   THREE THINGS LIVE HERE, AND ONLY THE THIRD KNOWS ABOUT SHOPIFY.
     1. The protocol. A copy of uncaffed-journey/shared/protocol.js, wrapped as
        a plain script because theme assets cannot import. If one changes the
        other must: `diff` them, the maths block is byte-for-byte the same.
     2. The views. Setup, Today, What's happening, Progress, Check-in, Day 30.
        Vanilla DOM, innerHTML templates, one delegated click handler.
     3. The store. load() and save(state), and nothing else. The Customer
        Account store writes a customer metafield (journey.state) through the
        Customer Account API after a PKCE bounce through Shopify's login. There
        is no server anywhere in this. If the section has no client id yet, the
        local store takes over and the page says so, so nothing silently lies.

   COPY. Every string has a default below and a matching schema setting in
   sections/unc-journey.liquid. The section's settings arrive in the boot JSON
   and override the defaults key by key, so a setting Shopify strips falls back
   to the same words rather than to "undefined".

   PROGRESSIVE ENHANCEMENT. The section server-renders today's view for the
   computed day. This element replaces it on upgrade.
   ============================================================================ */
(function () {
  'use strict';

  /* ==========================================================================
     1. THE PROTOCOL. Keep identical to uncaffed-journey/shared/protocol.js.
     ========================================================================== */
  var TOTAL_DAYS = 30;
  var DAYS_PER_STEP = 5;
  var MG_PER_RESET = 40;
  var DEFAULT_STRENGTH = 5; // 1 to 10; what the app assumes when a symptom has no strength
  var FLOW_PER_DAY = 2;
  var START_COUNT = 4;
  var PHASES = ['Phase I', 'Phase II', 'Phase III', 'Phase IV', 'Phase V', 'Phase VI'];

  function resetForDay(day) {
    if (day < 1 || day > TOTAL_DAYS) return 0;
    return Math.max(0, START_COUNT - Math.floor((day - 1) / DAYS_PER_STEP));
  }
  function isStepDown(day) {
    return day > 1 && resetForDay(day) < resetForDay(day - 1);
  }
  function isDipDay(day) {
    return day > 1 && isStepDown(day - 1);
  }
  function phaseFor(day) {
    var caps = resetForDay(day);
    if (caps === 0) return { label: T.phase_zero, isZero: true };
    return { label: PHASES[Math.floor((day - 1) / DAYS_PER_STEP)] || '', isZero: false };
  }
  function dayInfo(day) {
    var reset = resetForDay(day);
    return {
      day: day,
      reset: reset,
      flow: FLOW_PER_DAY,
      caffeine: reset * MG_PER_RESET,
      phase: phaseFor(day),
      isStepDown: isStepDown(day),
      isDipDay: isDipDay(day),
      isFirst: day === 1,
      isLast: day === TOTAL_DAYS,
      nextIsDip: day < TOTAL_DAYS && isDipDay(day + 1),
    };
  }
  function peakCaffeine() {
    return START_COUNT * MG_PER_RESET;
  }
  function schedule() {
    var out = [];
    for (var d = 1; d <= TOTAL_DAYS; d++) out.push(dayInfo(d));
    return out;
  }
  /* The dip days, derived rather than listed, so a change to the step length
     cannot leave a stale list behind. */
  function dipDays() {
    var out = [];
    for (var d = 1; d <= TOTAL_DAYS; d++) if (isDipDay(d)) out.push(d);
    return out;
  }

  /* ==========================================================================
     COPY DEFAULTS. Mirror of the schema defaults in sections/unc-journey.liquid.
     ========================================================================== */
  var DEFAULTS = {
    eyebrow: 'Your journey',
    day_word: 'Day',
    of_word: 'of',
    unit: 'mg',
    zero_dose: 'No added caffeine',
    phase_zero: 'The reset',
    member_label: 'Founding member',
    tab_today: 'Today',
    tab_body: "What's happening",
    tab_progress: 'Progress',
    prev_label: 'Day before',
    next_label: 'Day after',
    back_today: 'Back to today',
    viewing_note: 'You are looking at day {day}. Today is day {today}.',

    setup_eyebrow: 'Before day one',
    setup_heading: 'Set up your 30 days',
    setup_intro:
      'Four quick questions so the tips fit your day. The taper itself is the same for everyone: 160mg down to none, in five-day steps.',
    q1_title: 'How much caffeine do you have on a normal day?',
    q1_a: '3 or more coffees, or energy drinks',
    q1_a_note: 'Day one is itself a step down for you.',
    q1_b: 'About 2 coffees a day',
    q1_b_note: 'Right at the starting dose.',
    q1_c: 'About 1 coffee a day',
    q1_c_note: 'The first few days may feel easy. That is fine.',
    q1_d: 'Less than one, or just tea or cola',
    q1_d_note: 'The early phase will be gentle for you.',
    q2_title: 'When do you usually have your caffeine?',
    q2_a: 'First thing, then through the morning',
    q2_b: 'Spread across the whole day',
    q2_c: 'I rely on an afternoon or evening hit',
    q2_c_note: 'We will keep an eye on your sleep tips.',
    q3_title: 'What do your meals look like?',
    q3_a: 'Three regular meals',
    q3_b: 'Usually breakfast and dinner',
    q3_c: 'I often skip breakfast',
    q3_c_note: 'Take one Flow with breakfast and one with lunch.',
    q4_title: 'How is your sleep right now?',
    q4_a: 'Not great. Takes a while to drop off',
    q4_b: 'Okay, could be better',
    q4_c: 'I sleep fine',
    start_label: 'Day one is',
    start_hint: 'The day you take your first capsules. If your kit has arrived, that is probably today.',
    start_button: 'Start day 1',
    sched_heading: 'Your 30-day schedule',
    col_phase: 'Phase (days)',
    col_reset: 'Reset',
    col_flow: 'Flow',
    sched_note: 'Flow carries no added caffeine. Two a day for all thirty days, including the ten after the caffeine has gone.',
    high_head: 'Day one is a step down for you',
    high_body:
      'You are coming from above the starting dose, so the first day or two can feel like the dip rather than like nothing. It settles, and the steps after that are the same size for everyone.',
    low_head: 'The first phase may feel easy',
    low_body:
      'You already have less than the starting dose, so the first few days should feel comfortable. Follow the same steps down to none and the reset works exactly the same way.',

    coach_first:
      "Day one. Today's dose is about two coffees' worth, so for most people it feels completely normal. That is the point: no shock to the system.",
    coach_last: 'Last day. You are back at your own baseline. You did the whole thing.',
    coach_zero_first:
      'This is the big one: the first full day with no added caffeine. Go gently, keep it light, drink water. It lifts from here.',
    coach_dip:
      "Today is the dip. It is the day after a step down, so this is expected and normal. Water, an early night, and it passes.",
    coach_step:
      'Step-down day. You are taking one capsule fewer from today. The dip usually lands tomorrow, not now, so plan a lighter day tomorrow.',
    coach_zero: 'No added caffeine, and holding. This is where the reset actually happens. Keep taking Flow.',
    coach_half: 'Halfway. Most people find this stretch easier than the first week. Nicely done.',
    coach_early: 'Settling in. Same caffeine as a strong coffee, so you should feel completely normal.',
    coach_default: 'On track. One day at a time. The taper is doing the work.',

    capsules_heading: "Today's capsules",
    reset_name: 'Reset',
    flow_name: 'Flow',
    reset_sub: '{n} today, across the morning. Nothing after 2pm.',
    flow_sub: '2 today: one with breakfast, one with lunch.',
    taken_label: 'Taken',
    mark_taken: 'Mark as taken',
    timing_heading: 'When to take them',
    reset_timing_head: 'Reset, mornings only',
    reset_timing:
      'Spread {n} across the morning with water, roughly two hours apart: think 8am, 10am, midday. Nothing after 2pm, or the caffeine can reach your sleep.',
    flow_timing_head: 'Flow, twice a day',
    flow_three: 'Take one Flow with breakfast and one with lunch.',
    flow_two: 'Take one Flow with breakfast and one with lunch.',
    flow_skip: 'Take one Flow with breakfast and one with lunch.',
    flow_timing_tail: 'Having the second one with food is gentler on your stomach.',
    tomorrow_warning:
      'Heads up for tomorrow. The day after a step down is often the heavier one. An early night tonight sets you up well.',
    day30_button: "You've reached day 30. See your finish",

    checkin_heading: 'How are you feeling?',
    checkin_intro: 'Takes ten seconds. It helps you see your own pattern across the thirty days.',
    mood_label: 'Overall today',
    symptoms_label: 'Anything you are noticing?',
    symptoms: 'Headache, Tired or low energy, Foggy focus, Irritable, Low mood, Cravings, Poor sleep, Feeling good',
    good_symptom: 'Feeling good',
    caffeine_label: 'Any other caffeine today?',
    caffeine_hint: "Not counting your capsules. Decaf and herbal tea don't count.",
    caffeine_none: 'Nothing else',
    caffeine_total: 'About {mg}mg on top of your capsules.',
    caffeine_total_other: 'Something else is logged but not counted in that.',
    caffeine_less: 'One fewer {drink}',
    caffeine_more: 'One more {drink}',
    caffeine_drinks:
      'coffee|Coffee|a cup or mug|100\ntea|Tea|a cup|40\ncola|Cola or soft drink|a can|40\nenergy|Energy drink|a can, a big can counts as two|80\npre|Pre-workout or caffeine tablet|a serving|200\nother|Something else|mg unknown|',
    save_label: 'Save check-in',
    checked_in: 'Checked in for today',
    update_label: 'Update',
    checkin_cta: 'How are you feeling today?',
    checkin_past: 'Log how day {day} went',
    checkin_future: 'You can check in once you reach this day.',

    body_heading: "What's happening",
    body_sub: 'Day {day}, inside your body right now.',
    body_teaser: "What's happening today",
    why_label: 'Why is this happening',
    hide_why: 'Hide the why',
    curve_heading: 'Your caffeine curve',
    curve_sub: 'mg of added caffeine per day across the thirty. The dot is today.',
    method_heading: 'How to do it well',
    method:
      'Do not top up with coffee, tea, energy drinks or anything else with caffeine in it. It puts the clock back.\nCheck for hidden caffeine. Some pain relievers, pre-workouts, herbal supplements, green tea and chocolate all carry it.\nKeep the habit. Take your capsules with a decaf or another caffeine-free drink, so the ritual stays and only the caffeine goes.\nCome down in steps rather than stopping outright. That is what this is.\nDrink plenty of water, particularly on the heavier days.\nRest where you can. The dips are short.',
    help_heading: 'When to check with a doctor',
    help_intro: 'This is general wellbeing support, not medical advice. If you are unsure, speak to a pharmacist or your GP.',
    help:
      'Symptoms are severe, or last well beyond a couple of weeks.\nYou get migraines and this headache is not like your usual one.\nYou are pregnant or breastfeeding.\nYou are managing a heart condition or blood pressure.\nAnything feels wrong in a way that does not match what is described here.',

    first_head: 'Day one',
    first_body:
      'Nothing to push through today. You are starting at roughly what a two coffee habit already gives you, in a form that can be stepped down precisely.',
    first_why:
      'A taper works from where you actually are. Starting at your usual level means the first reduction lands on a body that has not already been knocked about.',
    step_head: 'The dose steps down today',
    step_body:
      'Today usually feels fine. If a dip comes it tends to land tomorrow rather than now, so it is worth keeping tomorrow a little lighter.',
    step_why:
      'Caffeine takes roughly a day to clear, so the effect of today lands 20 to 50 hours from now. That is why tomorrow tends to be the heavier day.',
    dip_head: 'This is the dip, and it is expected',
    dip_body:
      'Tired, foggy or headachy today is the day after a step down doing what it usually does. It normally passes within a day or two.',
    dip_why:
      'A dose change takes about a day to show its full effect, so the day after a cut is usually heavier than the cut itself. With less caffeine on board, the tiredness it was masking comes through louder for a while.',
    zero_first_head: 'The first full day at zero',
    zero_first_body:
      'No added caffeine from here. If today feels heavy, that is on schedule. Water, an earlier night, and keep the day light if you can.',
    zero_head: 'No added caffeine',
    zero_body:
      'This is the stretch that does the resetting. Most people find it gets easier from here rather than harder.',
    zero_why:
      'Given time without caffeine, the adaptations your brain made to cope with it start to unwind. Sensitivity usually begins returning within about five to seven days at zero.',
    steady_head: 'A steady stretch',
    steady_body:
      'Between the step downs most people feel level. Your body is catching up to the lower dose before the next one.',
    steady_why:
      'Each five day plateau gives you time to settle, so the next reduction lands on a body that is already most of the way there.',

    progress_heading: 'Your progress',
    progress_sub: '{n} of 30 days checked in.',
    stat_days: 'Days checked in',
    stat_phase: 'Current phase',
    stat_dips: 'Dips cleared',
    stat_dips_sub: 'Days {list}',
    stat_cut: 'Caffeine reduction',
    stat_cut_sub: 'lower than day one',
    calendar_heading: '30-day calendar',
    calendar_help: 'Tap a day to open it. The number is the day; the small figure is the dose.',
    legend_today: 'Today',
    legend_done: 'Checked in',
    legend_dip: 'Dip day, expected',
    symptoms_heading: 'What you have noticed',
    symptoms_empty: 'No check-ins yet. Your patterns will show up here.',
    strength_mild: 'Mild',
    strength_strong: 'Strong',
    strength_aria: '{symptom}, strength out of 10',
    chart_hint: 'Drag across the chart to see each day.',
    chart_aria: 'Strength of {symptom} by day',
    readout_strength: 'Day {day}: {symptom} {n}/10',
    readout_none: 'Day {day}: checked in, no {symptom}',
    readout_missed: 'Day {day}: no check-in',
    readout_future: 'Day {day}: still to come',
    stat_noticed: 'days noticed',
    stat_noticed_one: 'day noticed',
    stat_avg: 'average strength /10',
    stat_peak: 'strongest, day {day}',
    log_today: "Log today's check-in",

    done_eyebrow: 'Day 30, complete',
    done_heading: 'You did it.',
    done_body:
      "Thirty days ago caffeine was running your mornings. You have reset that, gradually and properly, all the way to none. Your body is running on its own energy now.",
    done_stats_heading: 'Your 30 days',
    stat_logged: 'days checked in',
    stat_taper: 'mg of caffeine, tapered to none',
    stat_wall: 'got through the first full day at zero, the hardest one',
    stat_good: 'days you marked feeling good',
    payoff_heading: 'Now, your first coffee back',
    payoff_body:
      'Your caffeine sensitivity is reset, so one cup will land like it used to. The trick to keeping it that way: make caffeine a choice, not a routine. Have it when you want the lift, not every morning on autopilot.',
    ref_eyebrow: 'Pass it on',
    ref_heading: 'Give a friend their reset',
    ref_body:
      'You know someone who runs on coffee they do not enjoy any more. Share your founding code and they start with a welcome from us.',
    ref_prefix: 'RESET',
    ref_copy: 'Copy invite',
    ref_copied: 'Copied',
    ref_share:
      'I just finished a 30-day caffeine reset with Uncaffed and coffee actually works again. My founding code: {code}',
    ref_note: 'Your code is tied to your founding number.',
    see_journey: 'See your full journey',
    back_30: 'Back to day 30',

    sync_saving: 'Saving',
    sync_saved: 'Saved to your account',
    sync_local: 'Saved on this device only',
    sync_error: 'Could not save to your account.',
    sync_retry: 'Retry',
    disclaimer: 'General wellbeing support, not medical advice.',
  };
  var T = DEFAULTS;

  /* ==========================================================================
     HELPERS
     ========================================================================== */
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  function fill(str, map) {
    return String(str).replace(/\{(\w+)\}/g, function (m, k) {
      return map[k] != null ? map[k] : m;
    });
  }
  function lines(str) {
    return String(str || '')
      .split('\n')
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }
  function list(str) {
    return String(str || '')
      .split(',')
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }
  /* ---- caffeine log (checkins[day].caffeine = { items: [{ k, n, mgEach, mg }], at }) ----
     Written by the iOS app too. items [] = "Nothing else"; no field = not answered;
     `other` has mgEach and mg null. mg is a snapshot taken when it was logged. */
  function cfDrinks() {
    return lines(T.caffeine_drinks).map(function (l) {
      var p = l.split('|');
      var mg = parseFloat(p[3]);
      return { k: (p[0] || '').trim(), name: (p[1] || '').trim(), unit: (p[2] || '').trim(), mg: isNaN(mg) ? null : mg };
    });
  }
  function cfFromEntry(c) {
    var d = { none: false, n: {}, each: {}, extra: [], answered: false };
    var known = cfDrinks().map(function (x) { return x.k; });
    var items = null;
    if (Array.isArray(c)) {
      // the app's first test format: a plain list of keys, one of each
      items = c.map(function (k) { return { k: k, n: 1 }; });
    } else if (c && Array.isArray(c.items)) {
      items = c.items;
    }
    if (!items) return d;
    d.answered = true;
    if (!items.length) {
      d.none = true;
      return d;
    }
    items.forEach(function (it) {
      if (!it || !it.k) return;
      if (known.indexOf(it.k) < 0) {
        d.extra.push(it);
        return;
      }
      d.n[it.k] = Math.max(0, Math.round(+it.n || 0));
      if (it.mgEach !== undefined) d.each[it.k] = it.mgEach;
    });
    return d;
  }
  function cfToEntry(cf, prev) {
    if (cf.none) return { items: [], at: prev && prev.items && !prev.items.length && prev.at ? prev.at : new Date().toISOString() };
    var items = [];
    cfDrinks().forEach(function (d) {
      var n = cf.n[d.k] | 0;
      if (n < 1) return;
      var each = cf.each[d.k] !== undefined ? cf.each[d.k] : d.mg;
      items.push({ k: d.k, n: n, mgEach: each, mg: each == null ? null : n * each });
    });
    cf.extra.forEach(function (it) { items.push(it); });
    if (!items.length) return null;
    var same = prev && Array.isArray(prev.items) && JSON.stringify(prev.items) === JSON.stringify(items) && prev.at;
    return { items: items, at: same ? prev.at : new Date().toISOString() };
  }

  /* ---- merging with what is stored ----------------------------------------
     The whole record is one metafield, so a tab that has been open for hours
     would overwrite what the app wrote since. Before every write the page now
     re-reads the stored record and merges: per day the newer check-in wins (by
     `at`), the caffeine log inside it by its own `at`; capsule ticks, profile and
     start date the visitor changed on this page win, everything else (including
     keys this page does not know, such as `research`) comes from the store. */
  function freshDirty() {
    return { profile: {}, startDate: false, reset: {}, flow: {} };
  }
  function stampOf(x) {
    return (x && typeof x.at === 'string' && x.at) || '';
  }
  function mergeDay(l, r) {
    var lw = stampOf(l) >= stampOf(r) ? l : r;
    var lo = lw === l ? r : l;
    var res = Object.assign({}, lo, lw);
    if (!('severity' in lw)) delete res.severity;
    var wc = lw.caffeine;
    var oc = lo.caffeine;
    if (wc && oc) res.caffeine = stampOf(oc) > stampOf(wc) ? oc : wc;
    else if (!wc && oc) {
      if (stampOf(oc) > stampOf(lw)) res.caffeine = oc;
      else delete res.caffeine;
    }
    return res;
  }
  function mergeStates(remote, local, dirty, base) {
    if (!remote || typeof remote !== 'object') return local;
    /* DELETED IN THE APP. `deletedAt` (ISO, top level) is set when the person deletes
       their journey or account, or starts over, and stays set afterwards. Anything
       this page holds from before it is dropped: check-ins by their own `at`, and the
       capsule ticks, profile and start date (no per-day times) when this copy was last
       saved before it. Edits made on this page since then carry a newer updatedAt. */
    var del = remote.deletedAt || local.deletedAt || '';
    if (del && del < (local.deletedAt || '')) del = local.deletedAt;
    if (del) {
      var kept = {};
      Object.keys(local.checkins || {}).forEach(function (d) {
        if (stampOf(local.checkins[d]) >= del) kept[d] = local.checkins[d];
      });
      /* `base` is the updatedAt of the stored record this copy was built on (a commit
         bumps local.updatedAt, so that cannot say how old the rest of the copy is). */
      var stale = (base == null ? local.updatedAt || '' : base) < del;
      local = Object.assign({}, local, { checkins: kept });
      if (stale) {
        var keepR = {};
        var keepF = {};
        var keepP = {};
        Object.keys(dirty.reset).forEach(function (d) { if (d in (local.resetTaken || {})) keepR[d] = local.resetTaken[d]; });
        Object.keys(dirty.flow).forEach(function (d) { if (d in (local.flowTaken || {})) keepF[d] = local.flowTaken[d]; });
        Object.keys(dirty.profile).forEach(function (k) { if (k in (local.profile || {})) keepP[k] = local.profile[k]; });
        local = Object.assign(local, {
          resetTaken: keepR,
          flowTaken: keepF,
          profile: keepP,
          startDate: dirty.startDate ? local.startDate : null,
          started: dirty.startDate ? local.started : false,
        });
      }
      var rk = {};
      Object.keys(remote.checkins || {}).forEach(function (d) {
        if (stampOf(remote.checkins[d]) >= del) rk[d] = remote.checkins[d];
      });
      remote = Object.assign({}, remote, { checkins: rk });
    }
    var out = Object.assign({}, local, remote);
    if (del) out.deletedAt = del;
    out.v = Math.max(remote.v || 1, local.v || 1);
    out.started = !!(remote.started || local.started);
    out.startDate = dirty.startDate ? local.startDate : remote.startDate || local.startDate || null;
    var lp = local.profile || {};
    var rp = remote.profile || {};
    out.profile = {};
    Object.keys(Object.assign({}, lp, rp)).forEach(function (k) {
      out.profile[k] = dirty.profile[k] ? lp[k] : rp[k] != null ? rp[k] : lp[k];
    });
    var lc = local.checkins || {};
    var rc = remote.checkins || {};
    out.checkins = {};
    Object.keys(Object.assign({}, lc, rc)).forEach(function (d) {
      out.checkins[d] = lc[d] && rc[d] ? mergeDay(lc[d], rc[d]) : lc[d] || rc[d];
    });
    ['reset', 'flow'].forEach(function (w) {
      var key = w + 'Taken';
      var lm = local[key] || {};
      var rm = remote[key] || {};
      out[key] = {};
      Object.keys(Object.assign({}, lm, rm)).forEach(function (d) {
        out[key][d] = dirty[w][d] ? lm[d] : d in rm ? rm[d] : lm[d];
      });
    });
    var lu = local.updatedAt || '';
    var ru = remote.updatedAt || '';
    out.updatedAt = lu > ru ? lu : ru || lu || null;
    return out;
  }

  function pad4(n) {
    return String(n).padStart(4, '0');
  }
  function isoDate(d) {
    var y = d.getFullYear();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }
  /* Whole days from a YYYY-MM-DD start to today, in local time, so a start of
     "today" is day 1 at any hour and day 2 begins at local midnight. */
  function dayFromStart(start) {
    if (!start) return null;
    var p = start.split('-');
    var s = new Date(+p[0], +p[1] - 1, +p[2]);
    var n = new Date();
    var t = new Date(n.getFullYear(), n.getMonth(), n.getDate());
    var diff = Math.round((t - s) / 86400000);
    return diff + 1;
  }
  var MOODS = ['\u{1F623}', '\u{1F615}', '\u{1F610}', '\u{1F642}', '\u{1F600}'];

  /* ==========================================================================
     COPY BY DAY. The same branching as shared/content.js, on the settings.
     ========================================================================== */
  function bodyNote(info) {
    if (info.isFirst) return { headline: T.first_head, body: T.first_body, why: T.first_why };
    if (info.isDipDay) {
      return {
        headline: info.caffeine === 0 ? T.zero_first_head : T.dip_head,
        body: info.caffeine === 0 ? T.zero_first_body : T.dip_body,
        why: T.dip_why,
      };
    }
    if (info.isStepDown) return { headline: T.step_head, body: T.step_body, why: T.step_why };
    if (info.phase.isZero) return { headline: T.zero_head, body: T.zero_body, why: T.zero_why };
    return { headline: T.steady_head, body: T.steady_body, why: T.steady_why };
  }
  function coachLine(info) {
    if (info.isFirst) return T.coach_first;
    if (info.isLast) return T.coach_last;
    if (info.isDipDay && info.caffeine === 0) return T.coach_zero_first;
    if (info.isDipDay) return T.coach_dip;
    if (info.isStepDown) return T.coach_step;
    if (info.phase.isZero) return T.coach_zero;
    if (info.day > DAYS_PER_STEP * 2 && info.day <= DAYS_PER_STEP * 3) return T.coach_half;
    if (info.day <= DAYS_PER_STEP) return T.coach_early;
    return T.coach_default;
  }
  function doseLabel(info) {
    return info.caffeine === 0 ? T.zero_dose : info.caffeine + T.unit;
  }
  function flowLine(profile) {
    var m = profile && profile.mealPattern;
    return (m === 'skip' ? T.flow_skip : m === 'two' ? T.flow_two : T.flow_three) + ' ' + T.flow_timing_tail;
  }

  /* ==========================================================================
     3. THE STORE
     ========================================================================== */
  var CACHE_KEY = 'unc_journey_state';

  function readJSON(storage, key) {
    try {
      var s = storage.getItem(key);
      return s ? JSON.parse(s) : null;
    } catch (e) {
      return null;
    }
  }
  function writeJSON(storage, key, v) {
    try {
      storage.setItem(key, JSON.stringify(v));
    } catch (e) {
      /* private mode or full: the metafield is the record, this is a cache */
    }
  }

  /* Local only. Used when the section has no client id, and as the cache in
     front of the account store so the page paints before the network answers. */
  function LocalStore() {
    this.kind = 'local';
  }
  LocalStore.prototype.load = function () {
    return Promise.resolve(readJSON(localStorage, CACHE_KEY));
  };
  LocalStore.prototype.save = function (state) {
    writeJSON(localStorage, CACHE_KEY, state);
    return Promise.resolve(true);
  };

  /* The Customer Account API, from the browser, with no server.

     Shopify's login is OAuth with PKCE, which exists precisely so a public
     client in a browser can get a token without a secret. The customer is
     already signed in to Shopify (that is how they got past the Liquid gate),
     so the bounce is a redirect out and straight back with a code. The code is
     swapped for a token, the token is kept for this tab only, and every read
     and write is one GraphQL call to the customer's own metafield. */
  function CustomerAccountStore(cfg) {
    this.kind = 'account';
    this.cfg = cfg;
    var base = 'https://shopify.com/authentication/' + cfg.shopId;
    this.authorizeUrl = cfg.authorizeUrl || base + '/oauth/authorize';
    this.tokenUrl = cfg.tokenUrl || base + '/oauth/token';
    this.apiUrl =
      cfg.apiUrl || 'https://shopify.com/' + cfg.shopId + '/account/customer/api/' + (cfg.apiVersion || '2026-07') + '/graphql';
    this.redirectUri = location.origin + location.pathname;
    this.customerId = cfg.customerId || null;
  }

  function b64url(bytes) {
    var s = '';
    for (var i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function randomString() {
    var a = new Uint8Array(32);
    crypto.getRandomValues(a);
    return b64url(a);
  }
  function sha256url(str) {
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(str)).then(function (buf) {
      return b64url(new Uint8Array(buf));
    });
  }

  CustomerAccountStore.prototype.token = function () {
    var self = this;
    var params = new URLSearchParams(location.search);
    if (params.get('code')) return this.exchange(params);
    var t = readJSON(sessionStorage, 'unc_jy_token');
    if (t && t.exp > Date.now() + 30000) return Promise.resolve(t.access_token);
    /* One bounce per tab. If it comes back without a code the page shows the
       cache and a retry link rather than redirecting forever. */
    if (sessionStorage.getItem('unc_jy_bounced')) return Promise.reject(new Error('unauthenticated'));
    return this.bounce();
  };

  CustomerAccountStore.prototype.bounce = function () {
    var self = this;
    var verifier = randomString();
    var state = randomString();
    var nonce = randomString();
    return sha256url(verifier).then(function (challenge) {
      writeJSON(sessionStorage, 'unc_jy_pkce', { verifier: verifier, state: state, nonce: nonce });
      sessionStorage.setItem('unc_jy_bounced', '1');
      var u = new URL(self.authorizeUrl);
      u.searchParams.set('scope', 'openid email customer-account-api:full');
      u.searchParams.set('client_id', self.cfg.clientId);
      u.searchParams.set('response_type', 'code');
      u.searchParams.set('redirect_uri', self.redirectUri);
      u.searchParams.set('state', state);
      u.searchParams.set('nonce', nonce);
      u.searchParams.set('code_challenge', challenge);
      u.searchParams.set('code_challenge_method', 'S256');
      location.assign(u.toString());
      /* Never resolves: the page is leaving. */
      return new Promise(function () {});
    });
  };

  CustomerAccountStore.prototype.exchange = function (params) {
    var self = this;
    var pk = readJSON(sessionStorage, 'unc_jy_pkce') || {};
    var clean = function () {
      try {
        history.replaceState({}, '', location.pathname);
      } catch (e) {}
    };
    if (!pk.verifier || params.get('state') !== pk.state) {
      clean();
      return Promise.reject(new Error('state mismatch'));
    }
    var body = new URLSearchParams();
    body.set('grant_type', 'authorization_code');
    body.set('client_id', this.cfg.clientId);
    body.set('redirect_uri', this.redirectUri);
    body.set('code', params.get('code'));
    body.set('code_verifier', pk.verifier);
    return fetch(this.tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    })
      .then(function (r) {
        return r.json().then(function (j) {
          if (!r.ok || !j.access_token) throw new Error(j.error_description || j.error || 'token');
          return j;
        });
      })
      .then(function (j) {
        writeJSON(sessionStorage, 'unc_jy_token', {
          access_token: j.access_token,
          exp: Date.now() + (j.expires_in || 3600) * 1000,
        });
        sessionStorage.removeItem('unc_jy_pkce');
        clean();
        return j.access_token;
      });
  };

  CustomerAccountStore.prototype.gql = function (query, variables) {
    var self = this;
    return this.token().then(function (tok) {
      return fetch(self.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: tok },
        body: JSON.stringify({ query: query, variables: variables || {} }),
      }).then(function (r) {
        if (r.status === 401 || r.status === 403) {
          sessionStorage.removeItem('unc_jy_token');
          throw new Error('unauthenticated');
        }
        return r.json();
      });
    });
  };

  CustomerAccountStore.prototype.load = function () {
    var self = this;
    return this.gql('query { customer { id metafield(namespace: "journey", key: "state") { value } } }').then(function (j) {
      if (j.errors && j.errors.length) throw new Error(j.errors[0].message);
      var c = j.data && j.data.customer;
      if (!c) throw new Error('no customer');
      self.customerId = c.id;
      var v = c.metafield && c.metafield.value;
      return v ? JSON.parse(v) : null;
    });
  };

  CustomerAccountStore.prototype.save = function (state) {
    var self = this;
    var go = function () {
      return self
        .gql(
          'mutation ($m: [MetafieldsSetInput!]!) { metafieldsSet(metafields: $m) { metafields { key } userErrors { field message } } }',
          {
            m: [
              {
                ownerId: self.customerId,
                namespace: 'journey',
                key: 'state',
                type: 'json',
                value: JSON.stringify(state),
              },
            ],
          }
        )
        .then(function (j) {
          var errs = (j.errors || []).concat((j.data && j.data.metafieldsSet && j.data.metafieldsSet.userErrors) || []);
          if (errs.length) throw new Error(errs[0].message);
          return true;
        });
    };
    if (this.customerId) return go();
    return this.load().then(go);
  };

  /* ==========================================================================
     2. THE VIEWS
     ========================================================================== */
  function emptyState() {
    return {
      v: 1,
      started: false,
      startDate: null,
      profile: { intake: null, coffeeTime: null, mealPattern: null, sleepConcern: null },
      checkins: {},
      resetTaken: {},
      flowTaken: {},
      updatedAt: null,
    };
  }

  var UncJourney = (function () {
    function UncJourney(el) {
      var boot = {};
      var copy = {};
      try {
        boot = JSON.parse(document.getElementById('unc-journey-boot').textContent);
      } catch (e) {}
      try {
        copy = JSON.parse(document.getElementById('unc-journey-copy').textContent);
      } catch (e) {}
      /* Only non-empty strings override, so a blanked setting cannot erase a
         label. Blank a sentence in the editor and the default comes back. */
      var over = {};
      Object.keys(copy).forEach(function (k) {
        if (typeof copy[k] === 'string' && copy[k].trim() !== '' && k in DEFAULTS) over[k] = copy[k];
      });
      T = Object.assign({}, DEFAULTS, over);
      this.el = el;
      this.boot = boot;
      this.state = emptyState();
      this.view = 'today';
      this.viewDay = 1;
      this.draft = { mood: 3, symptoms: [], severity: {}, cf: cfFromEntry(null) };
      this.showWhy = false;
      this.dirty = freshDirty();
      this.sync = 'idle'; // idle | saving | saved | local | error
      this.copied = false;

      var cfg = boot.config || {};
      this.cache = new LocalStore();
      this.store = cfg.clientId && cfg.shopId && window.crypto && crypto.subtle ? new CustomerAccountStore(cfg) : null;
      if (!this.store) this.sync = 'local';

      el.addEventListener('click', this.onClick.bind(this));
      el.addEventListener('change', this.onChange.bind(this));
      el.addEventListener('input', this.onInput.bind(this));
      el.addEventListener('pointermove', this.onMove.bind(this));
      this.init();
    }

    var P = UncJourney.prototype;

    P.init = function () {
      var self = this;
      /* Paint from the cache first so nothing waits on the network. */
      this.cache.load().then(function (cached) {
        if (cached) self.adopt(cached);
        self.render();
        if (!self.store) return;
        self.sync = 'saving';
        self.renderSync();
        self.store
          .load()
          .then(function (remote) {
            if (remote && cached) {
              /* Merge, never pick one: an offline edit and a newer record from the
                 app can both be right, on different days. */
              var m = mergeStates(remote, cached, freshDirty(), cached.updatedAt || '');
              self.adopt(m);
              self.cache.save(self.state);
              if (JSON.stringify(m) !== JSON.stringify(remote)) return self.syncOut();
            } else if (remote) {
              self.adopt(remote);
              self.cache.save(self.state);
            } else if (cached && cached.updatedAt) {
              return self.syncOut();
            }
          })
          .then(function () {
            self.sync = 'saved';
            self.render();
          })
          .catch(function () {
            self.sync = 'error';
            self.render();
          });
      });
    };

    P.adopt = function (s) {
      this.state = Object.assign(emptyState(), s || {});
      this.base = (s && s.updatedAt) || '';
      var boot = this.boot;
      /* Shopify's own record of day one wins over nothing; a date the customer
         set on this page wins over Shopify's guess. */
      if (!this.state.startDate && boot.startDate) this.state.startDate = boot.startDate;
      this.viewDay = this.today() || 1;
    };

    P.today = function () {
      var d = dayFromStart(this.state.startDate);
      if (d == null) return null;
      return Math.max(1, Math.min(TOTAL_DAYS, d));
    };

    P.commit = function () {
      var self = this;
      this.state.updatedAt = new Date().toISOString();
      this.cache.save(this.state);
      if (!this.store) return;
      this.sync = 'saving';
      this.renderSync();
      clearTimeout(this.saveT);
      this.saveT = setTimeout(function () {
        self.syncOut()
          .then(function () {
            self.sync = 'saved';
            self.renderSync();
          })
          .catch(function () {
            self.sync = 'error';
            self.renderSync();
          });
      }, 500);
    };

    /* Read the stored record, merge this page's changes into it, write the result.
       Anything the merge brought in from the store is adopted, so the page shows it. */
    P.syncOut = function () {
      var self = this;
      var snap = this.dirty;
      this.dirty = freshDirty();
      return this.store
        .load()
        .then(function (remote) {
          var before = JSON.stringify(self.state);
          var m = mergeStates(remote, self.state, snap, self.base);
          self.state = m;
          self.base = m.updatedAt || '';
          self.cache.save(m);
          if (self.view !== 'checkin' && JSON.stringify(m) !== before) self.render();
          return self.store.save(m);
        })
        .catch(function (err) {
          // not written: keep the changes marked, so the next attempt still treats them as this page's
          Object.keys(snap.profile).forEach(function (k) { self.dirty.profile[k] = true; });
          if (snap.startDate) self.dirty.startDate = true;
          ['reset', 'flow'].forEach(function (w) {
            Object.keys(snap[w]).forEach(function (d) { self.dirty[w][d] = true; });
          });
          throw err;
        });
    };

    P.go = function (view, day) {
      this.view = view;
      if (day) this.viewDay = day;
      this.showWhy = false;
      this.render();
      try {
        this.el.scrollIntoView({ block: 'start' });
      } catch (e) {}
    };

    /* ---- events ------------------------------------------------------- */
    P.onClick = function (e) {
      var b = e.target.closest('[data-act]');
      if (!b || !this.el.contains(b)) return;
      var act = b.getAttribute('data-act');
      var val = b.getAttribute('data-val');
      var s = this.state;
      switch (act) {
        case 'pick':
          s.profile[b.getAttribute('data-field')] = val;
          this.dirty.profile[b.getAttribute('data-field')] = true;
          this.render();
          break;
        case 'start':
          var input = this.el.querySelector('[data-start-date]');
          s.startDate = (input && input.value) || isoDate(new Date());
          s.started = true;
          this.dirty.startDate = true;
          this.commit();
          this.viewDay = this.today();
          this.go('today');
          break;
        case 'view':
          this.go(val);
          break;
        case 'day':
          this.go('today', +val);
          break;
        case 'prev':
          this.go(this.view, Math.max(1, this.viewDay - 1));
          break;
        case 'next':
          this.go(this.view, Math.min(TOTAL_DAYS, this.viewDay + 1));
          break;
        case 'today':
          this.go('today', this.today());
          break;
        case 'toggle':
          var map = val === 'reset' ? s.resetTaken : s.flowTaken;
          map[this.viewDay] = !map[this.viewDay];
          this.dirty[val === 'reset' ? 'reset' : 'flow'][this.viewDay] = true;
          this.commit();
          this.render();
          break;
        case 'checkin':
          var ex = s.checkins[this.viewDay];
          // severity (1-10 per symptom) is written by the iOS app; carry it through so a web re-save never wipes it
          this.draft = ex
            ? { mood: ex.mood, symptoms: (ex.symptoms || []).slice(), severity: Object.assign({}, ex.severity || {}) }
            : { mood: 3, symptoms: [], severity: {} };
          this.draft.cf = cfFromEntry(ex && ex.caffeine);
          // checked-in symptoms with no stored strength (older web check-ins) read as 5, like the app
          this.draft.symptoms.forEach(function (x) {
            if (x !== T.good_symptom && !(this.draft.severity[x] >= 1)) this.draft.severity[x] = DEFAULT_STRENGTH;
          }, this);
          this.go('checkin');
          break;
        case 'mood':
          this.draft.mood = +val;
          this.render();
          break;
        case 'symptom':
          var i = this.draft.symptoms.indexOf(val);
          if (i >= 0) {
            this.draft.symptoms.splice(i, 1);
            delete this.draft.severity[val];
          } else {
            this.draft.symptoms.push(val);
            if (val !== T.good_symptom) this.draft.severity[val] = DEFAULT_STRENGTH;
          }
          this.render();
          break;
        case 'cfnone':
          this.draft.cf.none = !this.draft.cf.none;
          if (this.draft.cf.none) {
            this.draft.cf.n = {};
            this.draft.cf.extra = [];
          }
          this.render();
          break;
        case 'cf':
          var cp = String(val).split(':');
          var cur = this.draft.cf.n[cp[0]] | 0;
          this.draft.cf.n[cp[0]] = Math.max(0, Math.min(20, cur + (+cp[1] || 0)));
          if (this.draft.cf.n[cp[0]] > 0) this.draft.cf.none = false;
          this.render();
          break;
        case 'sym':
          this.symSel = val;
          this.render();
          break;
        case 'col':
          this.showReadout(+val);
          break;
        case 'save':
          // start from the stored entry so any field the web does not know yet (the app adds some, e.g. caffeine today) survives a re-save
          var entry = Object.assign({}, s.checkins[this.viewDay], { mood: this.draft.mood, symptoms: this.draft.symptoms.slice(), at: new Date().toISOString() });
          // strengths for symptoms still selected only ("Feeling good" never has one); a missing one is 5; no key when empty, as the app does
          var sev = {};
          var dsev = this.draft.severity || {};
          entry.symptoms.forEach(function (x) {
            if (x !== T.good_symptom) sev[x] = dsev[x] >= 1 && dsev[x] <= 10 ? Math.round(dsev[x]) : DEFAULT_STRENGTH;
          });
          if (Object.keys(sev).length) entry.severity = sev;
          else delete entry.severity;
          // the caffeine log: written only when answered; an untouched, unanswered question leaves no field at all
          var cfe = cfToEntry(this.draft.cf, entry.caffeine);
          if (cfe) entry.caffeine = cfe;
          else delete entry.caffeine;
          s.checkins[this.viewDay] = entry;
          this.commit();
          this.go(this.viewDay === TOTAL_DAYS ? 'done' : 'today');
          break;
        case 'why':
          this.showWhy = !this.showWhy;
          this.render();
          break;
        case 'retry':
          this.sync = 'saving';
          this.renderSync();
          sessionStorage.removeItem('unc_jy_bounced');
          this.init();
          break;
        case 'copy':
          var self = this;
          try {
            navigator.clipboard.writeText(fill(T.ref_share, { code: this.refCode() }));
            this.copied = true;
            this.render();
            setTimeout(function () {
              self.copied = false;
              self.render();
            }, 2000);
          } catch (err) {}
          break;
      }
    };
    P.onChange = function () {
      /* The date input on setup: nothing to do until Start is pressed. */
    };

    /* A strength slider moved. Update the draft and its readout in place: a full
       render here would drop focus and break the drag. */
    P.onInput = function (e) {
      var r = e.target;
      if (!r || !r.getAttribute || r.getAttribute('data-sev') === null) return;
      var sym = r.getAttribute('data-sev');
      var n = Math.min(10, Math.max(1, Math.round(+r.value) || DEFAULT_STRENGTH));
      this.draft.severity[sym] = n;
      var out = r.parentNode && r.parentNode.parentNode && r.parentNode.parentNode.querySelector('[data-sev-out]');
      if (out) out.textContent = n + '/10';
    };

    /* Progress chart: dragging across the columns reads out each day. */
    P.onMove = function (e) {
      var host = e.target && e.target.closest && e.target.closest('[data-sc]');
      if (!host || !this.el.contains(host)) return;
      var box = host.getBoundingClientRect();
      if (!box.width) return;
      var d = Math.floor(((e.clientX - box.left) / box.width) * TOTAL_DAYS) + 1;
      this.showReadout(Math.min(TOTAL_DAYS, Math.max(1, d)));
    };

    function strengthOf(ci, sym) {
      var n = ci && ci.severity ? +ci.severity[sym] : 0;
      return n >= 1 && n <= 10 ? n : DEFAULT_STRENGTH;
    }

    P.readoutText = function (d, sym) {
      var ci = this.state.checkins[d];
      if (ci && (ci.symptoms || []).indexOf(sym) >= 0) return fill(T.readout_strength, { day: d, symptom: sym, n: strengthOf(ci, sym) });
      if (ci) return fill(T.readout_none, { day: d, symptom: sym.toLowerCase() });
      return fill(d > this.today() ? T.readout_future : T.readout_missed, { day: d });
    };

    P.showReadout = function (d) {
      var rd = this.el.querySelector('[data-rd]');
      var sym = rd && rd.getAttribute('data-sym');
      if (!rd || !sym) return;
      rd.textContent = this.readoutText(d, sym);
      var cols = this.el.querySelectorAll('[data-sc] [data-act="col"]');
      for (var i = 0; i < cols.length; i++) cols[i].classList.toggle('is-on', +cols[i].getAttribute('data-val') === d);
    };

    P.refCode = function () {
      return T.ref_prefix + pad4(this.boot.memberNo || 0);
    };

    /* ---- render --------------------------------------------------------- */
    P.render = function () {
      var html;
      if (!this.state.started) html = this.vSetup();
      else if (this.view === 'checkin') html = this.vCheckin();
      else if (this.view === 'progress') html = this.vProgress();
      else if (this.view === 'done') html = this.vDone();
      else if (this.view === 'body') html = this.vBody();
      else html = this.vToday();
      this.el.innerHTML = html;
      this.renderSync();
    };

    P.renderSync = function () {
      var host = this.el.querySelector('[data-sync]');
      if (!host) return;
      var k = this.sync;
      var txt = k === 'saving' ? T.sync_saving : k === 'saved' ? T.sync_saved : k === 'local' ? T.sync_local : k === 'error' ? T.sync_error : '';
      host.className = 'unc-jy__sync unc-jy__sync--' + k;
      host.innerHTML = esc(txt) + (k === 'error' ? ' <button type="button" class="unc-jy__link" data-act="retry">' + esc(T.sync_retry) + '</button>' : '');
    };

    P.topbar = function (label) {
      var today = this.today();
      var info = dayInfo(this.viewDay);
      var pill = T.day_word + ' ' + this.viewDay + ' / ' + TOTAL_DAYS + ': ' + info.phase.label + (info.caffeine ? ' (' + info.caffeine + T.unit + ')' : '');
      return (
        '<div class="unc-jy__top">' +
        '<span class="unc-jy__eyebrow">' + esc(T.eyebrow) + (this.boot.firstName ? ' · ' + esc(this.boot.firstName) : '') + '</span>' +
        '<nav class="unc-jy__tabs" aria-label="' + esc(T.eyebrow) + '">' +
        this.tab('today', T.tab_today) + this.tab('body', T.tab_body) + this.tab('progress', T.tab_progress) +
        '</nav>' +
        '<span class="unc-jy__pill">' + esc(pill) + '</span>' +
        '</div>' +
        '<p class="unc-jy__sync" data-sync></p>'
      );
    };
    P.tab = function (v, label) {
      var on = this.view === v || (v === 'today' && (this.view === 'checkin' || this.view === 'done'));
      return '<button type="button" class="unc-jy__tab" data-act="view" data-val="' + v + '"' + (on ? ' aria-current="page"' : '') + '>' + esc(label) + '</button>';
    };
    P.dayNav = function () {
      var today = this.today();
      var note = this.viewDay !== today ? '<span class="unc-jy__viewing">' + esc(fill(T.viewing_note, { day: this.viewDay, today: today })) + ' <button type="button" class="unc-jy__link" data-act="today">' + esc(T.back_today) + '</button></span>' : '';
      return (
        '<div class="unc-jy__daynav">' +
        '<button type="button" class="unc-jy__btn unc-jy__btn--ghost" data-act="prev"' + (this.viewDay <= 1 ? ' disabled' : '') + '>← ' + esc(T.prev_label) + '</button>' +
        note +
        '<button type="button" class="unc-jy__btn unc-jy__btn--ghost" data-act="next"' + (this.viewDay >= TOTAL_DAYS ? ' disabled' : '') + '>' + esc(T.next_label) + ' →</button>' +
        '</div>'
      );
    };
    P.card = function (inner, mod) {
      var cls = (mod || '')
        .split(' ')
        .filter(Boolean)
        .map(function (m) { return ' unc-jy__card--' + m; })
        .join('');
      return '<section class="unc-jy__card' + cls + '">' + inner + '</section>';
    };
    P.label = function (t) {
      return '<h2 class="unc-jy__label">' + esc(t) + '</h2>';
    };

    /* ---- setup ---------------------------------------------------------- */
    P.vSetup = function () {
      var p = this.state.profile;
      var q = function (field, title, opts) {
        return (
          '<div class="unc-jy__q"><h3 class="unc-jy__q-title">' + esc(title) + '</h3><div class="unc-jy__opts">' +
          opts
            .map(function (o) {
              var on = p[field] === o.v;
              return (
                '<button type="button" class="unc-jy__opt" data-act="pick" data-field="' + field + '" data-val="' + o.v + '" aria-pressed="' + on + '">' +
                '<span class="unc-jy__opt-l">' + esc(o.l) + '</span>' +
                (o.n ? '<span class="unc-jy__opt-n">' + esc(o.n) + '</span>' : '') +
                '</button>'
              );
            })
            .join('') +
          '</div></div>'
        );
      };
      var complete = p.intake && p.coffeeTime && p.mealPattern && p.sleepConcern;
      var note = '';
      if (p.intake === 'high') note = '<div class="unc-jy__note unc-jy__note--amber"><strong>' + esc(T.high_head) + '</strong><p>' + esc(T.high_body) + '</p></div>';
      else if (p.intake === 'low' || p.intake === 'minimal') note = '<div class="unc-jy__note unc-jy__note--green"><strong>' + esc(T.low_head) + '</strong><p>' + esc(T.low_body) + '</p></div>';

      var rows = [];
      for (var i = 0; i < PHASES.length; i++) {
        var first = i * DAYS_PER_STEP + 1;
        if (first > TOTAL_DAYS) break;
        var info = dayInfo(first);
        var last = Math.min(TOTAL_DAYS, first + DAYS_PER_STEP - 1);
        rows.push(
          '<tr' + (info.phase.isZero ? ' class="unc-jy__row--zero"' : '') + '><td>' + esc(info.phase.label) + ' (' + first + '–' + last + ')</td><td>' + (info.caffeine ? info.caffeine + T.unit : esc(T.zero_dose)) + '</td><td>' + info.flow + 'x</td></tr>'
        );
      }
      var start = this.state.startDate || this.boot.startDate || isoDate(new Date());
      var member = this.boot.memberNo ? '<span class="unc-jy__member">' + esc(T.member_label) + ' · ' + esc(T.ref_prefix) + ' #' + pad4(this.boot.memberNo) + '</span>' : '';

      return (
        this.topbar() +
        '<header class="unc-jy__hero"><span class="unc-jy__kicker">' + esc(T.setup_eyebrow) + '</span><h1 class="unc-jy__h1">' + esc(T.setup_heading) + '</h1><p class="unc-jy__intro">' + esc(T.setup_intro) + '</p>' + member + '</header>' +
        '<div class="unc-jy__grid unc-jy__grid--setup">' +
        '<div class="unc-jy__col">' +
        this.card(
          q('intake', T.q1_title, [
            { v: 'high', l: T.q1_a, n: T.q1_a_note },
            { v: 'target', l: T.q1_b, n: T.q1_b_note },
            { v: 'low', l: T.q1_c, n: T.q1_c_note },
            { v: 'minimal', l: T.q1_d, n: T.q1_d_note },
          ]) +
            q('coffeeTime', T.q2_title, [
              { v: 'early', l: T.q2_a },
              { v: 'spread', l: T.q2_b },
              { v: 'afternoon', l: T.q2_c, n: T.q2_c_note },
            ]) +
            q('mealPattern', T.q3_title, [
              { v: 'three', l: T.q3_a },
              { v: 'two', l: T.q3_b },
              { v: 'skip', l: T.q3_c, n: T.q3_c_note },
            ]) +
            q('sleepConcern', T.q4_title, [
              { v: 'poor', l: T.q4_a },
              { v: 'ok', l: T.q4_b },
              { v: 'good', l: T.q4_c },
            ])
        ) +
        '</div>' +
        '<div class="unc-jy__col">' +
        note +
        this.card(
          this.label(T.sched_heading) +
            '<table class="unc-jy__table"><thead><tr><th>' + esc(T.col_phase) + '</th><th>' + esc(T.col_reset) + '</th><th>' + esc(T.col_flow) + '</th></tr></thead><tbody>' + rows.join('') + '</tbody></table>' +
            '<p class="unc-jy__fine">' + esc(T.sched_note) + '</p>'
        ) +
        this.card(
          '<label class="unc-jy__label" for="unc-jy-start">' + esc(T.start_label) + '</label>' +
            '<input class="unc-jy__date" id="unc-jy-start" type="date" value="' + esc(start) + '" data-start-date>' +
            '<p class="unc-jy__fine">' + esc(T.start_hint) + '</p>' +
            '<button type="button" class="unc-jy__btn unc-jy__btn--primary unc-jy__btn--wide" data-act="start"' + (complete ? '' : ' disabled') + '>' + esc(T.start_button) + ' →</button>'
        ) +
        '<p class="unc-jy__fine unc-jy__fine--center">' + esc(T.disclaimer) + '</p>' +
        '</div></div>'
      );
    };

    /* ---- today ---------------------------------------------------------- */
    P.vToday = function () {
      var s = this.state;
      var d = this.viewDay;
      var today = this.today();
      var info = dayInfo(d);
      var note = bodyNote(info);
      var ci = s.checkins[d];
      var canCheck = d <= today;

      var capsule = function (name, sub, count, taken, key, outline) {
        var pills = '';
        for (var i = 0; i < count; i++) pills += '<span class="unc-jy__pillcap' + (outline ? ' unc-jy__pillcap--outline' : '') + '"></span>';
        return (
          '<button type="button" class="unc-jy__cap" data-act="toggle" data-val="' + key + '" aria-pressed="' + !!taken + '">' +
          '<span class="unc-jy__cap-pills">' + pills + '</span>' +
          '<span class="unc-jy__cap-body"><span class="unc-jy__cap-name">' + esc(name) + '</span><span class="unc-jy__cap-sub">' + esc(sub) + '</span></span>' +
          '<span class="unc-jy__cap-state">' + (taken ? '✓ ' + esc(T.taken_label) : esc(T.mark_taken)) + '</span>' +
          '</button>'
        );
      };

      var checkin;
      if (ci) {
        checkin =
          '<div class="unc-jy__done-ci"><span class="unc-jy__mood-big">' + MOODS[ci.mood - 1] + '</span><strong>' + esc(T.checked_in) + '</strong>' +
          ((ci.symptoms || []).length ? '<span class="unc-jy__fine">' + esc(ci.symptoms.join(' · ')) + '</span>' : '') +
          '<button type="button" class="unc-jy__link" data-act="checkin">' + esc(T.update_label) + '</button></div>';
      } else if (canCheck) {
        checkin = '<button type="button" class="unc-jy__btn unc-jy__btn--primary unc-jy__btn--wide" data-act="checkin">' + esc(d === today ? T.checkin_cta : fill(T.checkin_past, { day: d })) + '</button>';
      } else {
        checkin = '<p class="unc-jy__fine">' + esc(T.checkin_future) + '</p>';
      }

      return (
        this.topbar() +
        '<header class="unc-jy__hero"><span class="unc-jy__kicker' + (info.phase.isZero ? ' unc-jy__kicker--green' : '') + '">' + esc(info.phase.label) + ' · ' + esc(T.day_word) + ' ' + d + ' ' + esc(T.of_word) + ' ' + TOTAL_DAYS + '</span>' +
        '<h1 class="unc-jy__h1">' + esc(doseLabel(info)) + '</h1>' +
        '<p class="unc-jy__intro">' + esc(coachLine(info)) + '</p></header>' +
        this.dayNav() +
        (info.nextIsDip ? '<div class="unc-jy__note unc-jy__note--amber unc-jy__note--row">↗ <span>' + esc(T.tomorrow_warning) + '</span></div>' : '') +
        (d === TOTAL_DAYS ? '<button type="button" class="unc-jy__btn unc-jy__btn--green unc-jy__btn--wide" data-act="view" data-val="done">' + esc(T.day30_button) + '</button>' : '') +
        '<div class="unc-jy__grid">' +
        '<div class="unc-jy__col">' +
        this.card(
          this.label(T.capsules_heading) +
            (info.reset > 0 ? capsule(T.reset_name, fill(T.reset_sub, { n: info.reset }), info.reset, s.resetTaken[d], 'reset', true) : '') +
            capsule(T.flow_name, T.flow_sub, info.flow, s.flowTaken[d], 'flow', false)
        ) +
        this.card(this.label(canCheck && !ci ? T.checkin_heading : T.checkin_heading) + checkin, ci ? 'green' : '') +
        '</div>' +
        '<div class="unc-jy__col">' +
        this.card(
          '<span class="unc-jy__label unc-jy__label--gold">' + esc(T.body_teaser) + '</span><h3 class="unc-jy__h3">' + esc(note.headline) + '</h3><p>' + esc(note.body) + '</p>' +
            '<button type="button" class="unc-jy__link" data-act="why">' + esc(this.showWhy ? T.hide_why : T.why_label) + ' ' + (this.showWhy ? '▴' : '▾') + '</button>' +
            (this.showWhy ? '<p class="unc-jy__why">' + esc(note.why) + '</p>' : '') +
            '<button type="button" class="unc-jy__link unc-jy__link--more" data-act="view" data-val="body">' + esc(T.tab_body) + ' →</button>',
          info.isDipDay ? 'amber' : ''
        ) +
        this.card(
          this.label(T.timing_heading) +
            (info.reset > 0 ? '<div class="unc-jy__timing"><span class="unc-jy__pillcap unc-jy__pillcap--outline"></span><div><strong>' + esc(T.reset_timing_head) + '</strong><p>' + esc(fill(T.reset_timing, { n: info.reset })) + '</p></div></div>' : '') +
            '<div class="unc-jy__timing"><span class="unc-jy__pillcap"></span><div><strong>' + esc(T.flow_timing_head) + '</strong><p>' + esc(flowLine(s.profile)) + '</p></div></div>'
        ) +
        '</div></div>'
      );
    };

    /* ---- check-in ------------------------------------------------------- */
    P.vCheckin = function () {
      var self = this;
      var syms = list(T.symptoms);
      return (
        this.topbar() +
        '<header class="unc-jy__hero"><span class="unc-jy__kicker">' + esc(T.day_word) + ' ' + this.viewDay + '</span><h1 class="unc-jy__h1">' + esc(T.checkin_heading) + '</h1><p class="unc-jy__intro">' + esc(T.checkin_intro) + '</p></header>' +
        '<div class="unc-jy__narrow">' +
        this.card(
          this.label(T.mood_label) +
            '<div class="unc-jy__moods">' +
            MOODS.map(function (m, i) {
              return '<button type="button" class="unc-jy__mood" data-act="mood" data-val="' + (i + 1) + '" aria-pressed="' + (self.draft.mood === i + 1) + '" aria-label="' + (i + 1) + '">' + m + '</button>';
            }).join('') +
            '</div>' +
            this.label(T.symptoms_label) +
            '<div class="unc-jy__chips">' +
            syms
              .map(function (x) {
                var on = self.draft.symptoms.indexOf(x) >= 0;
                return '<button type="button" class="unc-jy__chip' + (x === T.good_symptom ? ' unc-jy__chip--good' : '') + '" data-act="symptom" data-val="' + esc(x) + '" aria-pressed="' + on + '">' + esc(x) + '</button>';
              })
              .join('') +
            '</div>' +
            self.draft.symptoms
              .filter(function (x) { return x !== T.good_symptom; })
              .map(function (x) {
                var n = self.draft.severity[x] >= 1 ? self.draft.severity[x] : DEFAULT_STRENGTH;
                return (
                  '<div class="unc-jy__sev"><div class="unc-jy__sev-h"><span>' + esc(x) + '</span><strong data-sev-out>' + n + '/10</strong></div>' +
                  '<div class="unc-jy__sev-r"><input type="range" min="1" max="10" step="1" value="' + n + '" data-sev="' + esc(x) + '" aria-label="' + esc(fill(T.strength_aria, { symptom: x })) + '"></div>' +
                  '<div class="unc-jy__sev-e"><span>' + esc(T.strength_mild) + '</span><span>' + esc(T.strength_strong) + '</span></div></div>'
                );
              })
              .join('') +
            this.cfCard() +
            '<button type="button" class="unc-jy__btn unc-jy__btn--primary unc-jy__btn--wide" data-act="save">' + esc(T.save_label) + '</button>'
        ) +
        '</div>'
      );
    };

    /* ---- any other caffeine today ---------------------------------------- */
    P.cfCard = function () {
      var cf = this.draft.cf;
      var mg = 0;
      var hasOther = false;
      cf.extra.forEach(function (it) {
        if (it.mg != null) mg += +it.mg || 0;
        else hasOther = true;
      });
      var rows = cfDrinks()
        .map(function (d) {
          var n = cf.n[d.k] | 0;
          var each = cf.each[d.k] !== undefined ? cf.each[d.k] : d.mg;
          if (n > 0 && each != null) mg += n * each;
          if (n > 0 && each == null) hasOther = true;
          var sub = d.mg != null ? d.unit + ', about ' + d.mg + 'mg' : d.unit;
          return (
            '<div class="unc-jy__cf-row' + (n > 0 ? ' is-on' : '') + '">' +
            '<div class="unc-jy__cf-t"><span class="unc-jy__cf-n">' + esc(d.name) + '</span><span class="unc-jy__cf-u">' + esc(sub) + '</span></div>' +
            '<div class="unc-jy__cf-step">' +
            '<button type="button" class="unc-jy__cf-b" data-act="cf" data-val="' + esc(d.k) + ':-1"' + (n < 1 ? ' disabled' : '') + ' aria-label="' + esc(fill(T.caffeine_less, { drink: d.name })) + '">&minus;</button>' +
            '<span class="unc-jy__cf-c" aria-live="polite">' + n + '</span>' +
            '<button type="button" class="unc-jy__cf-b" data-act="cf" data-val="' + esc(d.k) + ':1" aria-label="' + esc(fill(T.caffeine_more, { drink: d.name })) + '">+</button>' +
            '</div></div>'
          );
        })
        .join('');
      var total = '';
      if (!cf.none && (mg > 0 || hasOther)) {
        total = '<p class="unc-jy__fine unc-jy__cf-total">' + (mg > 0 ? esc(fill(T.caffeine_total, { mg: mg })) : '') + (hasOther ? ' ' + esc(T.caffeine_total_other) : '') + '</p>';
      }
      return (
        this.label(T.caffeine_label) +
        '<p class="unc-jy__fine unc-jy__cf-hint">' + esc(T.caffeine_hint) + '</p>' +
        '<div class="unc-jy__chips unc-jy__cf-none"><button type="button" class="unc-jy__chip unc-jy__chip--good" data-act="cfnone" aria-pressed="' + cf.none + '">' + esc(T.caffeine_none) + '</button></div>' +
        '<div class="unc-jy__cf">' + rows + '</div>' +
        total
      );
    };

    /* ---- what's happening ----------------------------------------------- */
    P.vBody = function () {
      var info = dayInfo(this.viewDay);
      var note = bodyNote(info);
      var li = function (arr) {
        return '<ul class="unc-jy__list">' + arr.map(function (m) { return '<li>' + esc(m) + '</li>'; }).join('') + '</ul>';
      };
      return (
        this.topbar() +
        '<header class="unc-jy__hero"><span class="unc-jy__kicker">' + esc(fill(T.body_sub, { day: this.viewDay })) + '</span><h1 class="unc-jy__h1">' + esc(T.body_heading) + '</h1></header>' +
        this.dayNav() +
        '<div class="unc-jy__grid">' +
        '<div class="unc-jy__col">' +
        this.card(
          '<h3 class="unc-jy__h3">' + esc(note.headline) + '</h3><p>' + esc(note.body) + '</p>' +
            '<button type="button" class="unc-jy__link" data-act="why">' + esc(this.showWhy ? T.hide_why : T.why_label) + ' ' + (this.showWhy ? '▴' : '▾') + '</button>' +
            (this.showWhy ? '<p class="unc-jy__why">' + esc(note.why) + '</p>' : ''),
          info.isDipDay ? 'amber' : ''
        ) +
        this.card(this.label(T.curve_heading) + '<p class="unc-jy__fine">' + esc(T.curve_sub) + '</p>' + this.curve()) +
        '</div>' +
        '<div class="unc-jy__col">' +
        this.card(this.label(T.method_heading) + li(lines(T.method))) +
        this.card(this.label(T.help_heading) + '<p class="unc-jy__fine">' + esc(T.help_intro) + '</p>' + li(lines(T.help)), 'red') +
        '</div></div>'
      );
    };

    P.curve = function () {
      var W = 320, H = 140, padL = 32, padB = 22, padT = 12, padR = 10;
      var maxC = peakCaffeine();
      var x = function (d) { return padL + ((d - 1) / (TOTAL_DAYS - 1)) * (W - padL - padR); };
      var y = function (c) { return padT + (1 - c / maxC) * (H - padT - padB); };
      var pts = schedule().map(function (i) { return x(i.day).toFixed(1) + ',' + y(i.caffeine).toFixed(1); }).join(' ');
      var cur = dayInfo(this.viewDay).caffeine;
      var grid = '';
      for (var c = 0; c <= maxC; c += MG_PER_RESET) {
        grid += '<line x1="' + padL + '" y1="' + y(c) + '" x2="' + (W - padR) + '" y2="' + y(c) + '" class="unc-jy__c-grid"/><text x="' + (padL - 6) + '" y="' + (y(c) + 3) + '" class="unc-jy__c-t" text-anchor="end">' + c + '</text>';
      }
      var xl = [1, 10, 20, 30].map(function (d) { return '<text x="' + x(d) + '" y="' + (H - 8) + '" class="unc-jy__c-t" text-anchor="middle">' + esc(T.day_word) + ' ' + d + '</text>'; }).join('');
      return (
        '<svg class="unc-jy__curve" viewBox="0 0 ' + W + ' ' + H + '" role="img" aria-label="' + esc(T.curve_heading) + '">' + grid +
        '<polygon points="' + padL + ',' + y(0) + ' ' + pts + ' ' + x(TOTAL_DAYS) + ',' + y(0) + '" class="unc-jy__c-area"/>' +
        '<polyline points="' + pts + '" class="unc-jy__c-line"/>' +
        '<line x1="' + x(this.viewDay) + '" y1="' + padT + '" x2="' + x(this.viewDay) + '" y2="' + (H - padB) + '" class="unc-jy__c-here"/>' +
        '<circle cx="' + x(this.viewDay) + '" cy="' + y(cur) + '" r="4.5" class="unc-jy__c-dot"/>' + xl + '</svg>'
      );
    };

    /* ---- progress ------------------------------------------------------- */
    P.vProgress = function () {
      var s = this.state;
      var today = this.today();
      var done = Object.keys(s.checkins).length;
      var info = dayInfo(today);
      var dips = dipDays();
      var cleared = dips.filter(function (d) { return d < today; });
      var cut = Math.round((1 - info.caffeine / peakCaffeine()) * 100);

      var tile = function (label, big, sub) {
        return '<div class="unc-jy__tile"><span class="unc-jy__tile-l">' + esc(label) + '</span><span class="unc-jy__tile-v">' + big + '</span>' + (sub ? '<span class="unc-jy__tile-s">' + esc(sub) + '</span>' : '') + '</div>';
      };
      var cells = schedule()
        .map(function (i) {
          var d = i.day;
          var isDone = !!s.checkins[d];
          var cls = 'unc-jy__cell' + (d === today ? ' unc-jy__cell--today' : '') + (isDone ? ' unc-jy__cell--done' : '') + (i.isDipDay ? ' unc-jy__cell--dip' : '') + (i.phase.isZero ? ' unc-jy__cell--zero' : '') + (d > today ? ' unc-jy__cell--future' : '');
          return '<button type="button" class="' + cls + '" data-act="day" data-val="' + d + '"><span class="unc-jy__cell-d">' + esc(T.day_word) + ' ' + d + '</span>' + (isDone ? '<span class="unc-jy__cell-ok">✓</span>' : '') + '<span class="unc-jy__cell-mg">' + (i.caffeine ? i.caffeine + T.unit : '0' + T.unit) + '</span></button>';
        })
        .join('');

      var counts = {};
      Object.keys(s.checkins).forEach(function (k) {
        (s.checkins[k].symptoms || []).forEach(function (x) { if (x !== T.good_symptom) counts[x] = (counts[x] || 0) + 1; });
      });
      var entries = Object.keys(counts).map(function (k) { return [k, counts[k]]; }).sort(function (a, b) { return b[1] - a[1]; });
      var sel = entries.length ? (counts[this.symSel] ? this.symSel : entries[0][0]) : '';
      var bars;
      if (!entries.length) {
        bars = '<p class="unc-jy__fine">' + esc(T.symptoms_empty) + '</p>';
      } else {
        var self = this;
        var chips = entries.map(function (e) {
          return '<button type="button" class="unc-jy__chip" data-act="sym" data-val="' + esc(e[0]) + '" aria-pressed="' + (e[0] === sel) + '">' + esc(e[0]) + '</button>';
        }).join('');
        var vals = [], peak = 0, peakDay = 0;
        var cols = schedule().map(function (i) {
          var d = i.day, ci = s.checkins[d];
          var inner, has = ci && (ci.symptoms || []).indexOf(sel) >= 0;
          if (has) {
            var n = strengthOf(ci, sel);
            vals.push(n);
            if (n > peak) { peak = n; peakDay = d; }
            inner = '<i class="unc-jy__sc-bar" style="height:' + n * 10 + '%"></i>';
          } else if (ci) {
            inner = '<i class="unc-jy__sc-ok"></i>';
          } else {
            inner = '<i class="unc-jy__sc-gap"></i>';
          }
          return '<button type="button" class="unc-jy__sc-col' + (d === today ? ' is-today' : '') + '" data-act="col" data-val="' + d + '" aria-label="' + esc(self.readoutText(d, sel)) + '"><span class="unc-jy__sc-t">' + inner + '</span>' +
            '<span class="unc-jy__sc-dip' + (i.isDipDay ? ' is-on' : '') + '"></span>' +
            '<span class="unc-jy__sc-d">' + ([1, 10, 20, 30].indexOf(d) >= 0 ? d : '') + '</span></button>';
        }).join('');
        var avg = vals.length ? Math.round((vals.reduce(function (a, b) { return a + b; }, 0) / vals.length) * 10) / 10 : 0;
        var stat = function (v, l) { return '<div class="unc-jy__sc-stat"><strong>' + v + '</strong><span>' + esc(l) + '</span></div>'; };
        bars =
          '<div class="unc-jy__chips">' + chips + '</div>' +
          '<div class="unc-jy__sc" data-sc role="group" aria-label="' + esc(fill(T.chart_aria, { symptom: sel.toLowerCase() })) + '">' + cols + '</div>' +
          '<p class="unc-jy__sc-rd" data-rd data-sym="' + esc(sel) + '" aria-live="polite">' + esc(T.chart_hint) + '</p>' +
          '<div class="unc-jy__sc-stats">' + stat(vals.length, vals.length === 1 ? T.stat_noticed_one : T.stat_noticed) + stat(avg, T.stat_avg) + stat(peak, fill(T.stat_peak, { day: peakDay })) + '</div>';
      }

      return (
        this.topbar() +
        '<header class="unc-jy__hero"><span class="unc-jy__kicker">' + esc(T.eyebrow) + '</span><h1 class="unc-jy__h1">' + esc(T.progress_heading) + '</h1><p class="unc-jy__intro">' + esc(fill(T.progress_sub, { n: done })) + '</p></header>' +
        '<div class="unc-jy__tiles">' +
        tile(T.stat_days, done + ' <small>/ ' + TOTAL_DAYS + '</small>') +
        tile(T.stat_phase, esc(info.phase.label) + (info.caffeine ? ' <small>(' + info.caffeine + T.unit + ')</small>' : '')) +
        tile(T.stat_dips, cleared.length + ' <small>/ ' + dips.length + '</small>', fill(T.stat_dips_sub, { list: dips.join(', ') })) +
        tile(T.stat_cut, cut + '%', T.stat_cut_sub) +
        '</div>' +
        this.card(this.label(T.calendar_heading) + '<p class="unc-jy__fine">' + esc(T.calendar_help) + '</p><div class="unc-jy__cal">' + cells + '</div>' +
          '<p class="unc-jy__legend"><span><i class="unc-jy__lg unc-jy__lg--today"></i>' + esc(T.legend_today) + '</span><span><i class="unc-jy__lg unc-jy__lg--done"></i>' + esc(T.legend_done) + '</span><span><i class="unc-jy__lg unc-jy__lg--dip"></i>' + esc(T.legend_dip) + '</span></p>') +
        '<div class="unc-jy__grid">' +
        '<div class="unc-jy__col">' + this.card(this.label(T.symptoms_heading) + bars) + '</div>' +
        '<div class="unc-jy__col">' + this.card(this.label(T.method_heading) + '<ul class="unc-jy__list">' + lines(T.method).map(function (m) { return '<li>' + esc(m) + '</li>'; }).join('') + '</ul>' +
          '<button type="button" class="unc-jy__btn unc-jy__btn--primary unc-jy__btn--wide" data-act="day" data-val="' + today + '">' + esc(T.log_today) + '</button>') + '</div>' +
        '</div>'
      );
    };

    /* ---- day 30 --------------------------------------------------------- */
    P.vDone = function () {
      var s = this.state;
      var logged = Object.keys(s.checkins).length;
      var good = Object.keys(s.checkins).filter(function (k) { return (s.checkins[k].symptoms || []).indexOf(T.good_symptom) >= 0; }).length;
      var firstZero = dipDays().filter(function (d) { return dayInfo(d).caffeine === 0; })[0];
      var wall = firstZero && !!s.checkins[firstZero];
      var stat = function (n, label, mod) {
        return '<div class="unc-jy__stat"><span class="unc-jy__stat-n' + (mod ? ' unc-jy__stat-n--' + mod : '') + '">' + n + '</span><span>' + esc(label) + '</span></div>';
      };
      var bars = [4, 3, 2, 1, 0].map(function (c, i) {
        var h = 12 + (c / 4) * 52;
        return '<rect x="' + (12 + i * 46) + '" y="' + (78 - h) + '" width="30" height="' + h + '" rx="2" class="' + (c === 0 ? 'unc-jy__f-zero' : 'unc-jy__f-bar') + '" style="opacity:' + (c === 0 ? 1 : 0.35 + i * 0.12) + '"/>';
      }).join('');
      var code = this.refCode();
      return (
        this.topbar() +
        '<div class="unc-jy__narrow unc-jy__center">' +
        '<svg viewBox="0 0 240 90" class="unc-jy__finish" aria-hidden="true">' + bars + '</svg>' +
        '<span class="unc-jy__kicker unc-jy__kicker--green">' + esc(T.done_eyebrow) + '</span>' +
        '<h1 class="unc-jy__h1 unc-jy__h1--big">' + esc(T.done_heading) + '</h1>' +
        '<p class="unc-jy__intro">' + esc(T.done_body) + '</p>' +
        this.card(this.label(T.done_stats_heading) + stat(logged + '<small>/' + TOTAL_DAYS + '</small>', T.stat_logged) + stat('160→0', T.stat_taper, 'plain') + (wall ? stat('✓', T.stat_wall, 'green') : '') + (good ? stat(good, T.stat_good, 'green') : ''), 'left') +
        this.card('<h3 class="unc-jy__h3">' + esc(T.payoff_heading) + '</h3><p>' + esc(T.payoff_body) + '</p>', 'green left') +
        (this.boot.memberNo
          ? '<div class="unc-jy__ref"><span class="unc-jy__label unc-jy__label--gold">' + esc(T.ref_eyebrow) + '</span><h3 class="unc-jy__h3">' + esc(T.ref_heading) + '</h3><p>' + esc(T.ref_body) + '</p>' +
            '<div class="unc-jy__ref-row"><code class="unc-jy__code">' + esc(code) + '</code><button type="button" class="unc-jy__btn unc-jy__btn--gold" data-act="copy">' + esc(this.copied ? T.ref_copied + ' ✓' : T.ref_copy) + '</button></div>' +
            '<p class="unc-jy__fine unc-jy__fine--dim">' + esc(T.ref_note) + '</p></div>'
          : '') +
        '<button type="button" class="unc-jy__btn unc-jy__btn--primary unc-jy__btn--wide" data-act="view" data-val="progress">' + esc(T.see_journey) + '</button>' +
        '<button type="button" class="unc-jy__link unc-jy__link--block" data-act="day" data-val="' + TOTAL_DAYS + '">' + esc(T.back_30) + '</button>' +
        '<p class="unc-jy__fine unc-jy__fine--center">' + (this.boot.memberNo ? esc(T.member_label) + ' · ' + esc(T.ref_prefix) + ' #' + pad4(this.boot.memberNo) + '<br>' : '') + esc(T.disclaimer) + '</p>' +
        '</div>'
      );
    };

    return UncJourney;
  })();

  function upgrade() {
    var els = document.querySelectorAll('unc-journey:not([data-ready])');
    for (var i = 0; i < els.length; i++) {
      els[i].setAttribute('data-ready', '');
      new UncJourney(els[i]);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', upgrade);
  else upgrade();
})();
